import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertTriangle, Scale, ShieldAlert } from 'lucide-react';

const DISCLAIMER_ACCEPTED_KEY = 'scratch_advisor_disclaimer_accepted';
const DISCLAIMER_VERSION = '1.0'; // Increment to force re-acceptance

export const DisclaimerModal = () => {
  const [open, setOpen] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    // Check if user has already accepted the current version of the disclaimer
    const acceptedVersion = localStorage.getItem(DISCLAIMER_ACCEPTED_KEY);
    if (acceptedVersion !== DISCLAIMER_VERSION) {
      setOpen(true);
    }
  }, []);

  const handleAccept = () => {
    if (acknowledged) {
      localStorage.setItem(DISCLAIMER_ACCEPTED_KEY, DISCLAIMER_VERSION);
      setOpen(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-[600px] max-w-[95vw] h-[90vh] sm:h-auto sm:max-h-[85vh] flex flex-col p-0 gap-0" 
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="disclaimer-modal"
      >
        {/* Header - Fixed */}
        <DialogHeader className="p-4 sm:p-6 pb-2 sm:pb-4 flex-shrink-0">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-amber-500/10 rounded-full">
              <ShieldAlert className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500" />
            </div>
            <DialogTitle className="text-lg sm:text-xl">Important Legal Disclaimer</DialogTitle>
          </div>
          <DialogDescription className="text-left text-xs sm:text-sm">
            Please read and acknowledge the following disclaimer before using this application.
          </DialogDescription>
        </DialogHeader>
        
        {/* Scrollable Content - Flexible */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 min-h-0">
          <div className="space-y-3 sm:space-y-4 text-xs sm:text-sm text-muted-foreground pb-4">
            <div className="p-3 sm:p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="font-semibold text-amber-600 dark:text-amber-400 text-xs sm:text-sm">
                  FOR ENTERTAINMENT PURPOSES ONLY
                </p>
              </div>
            </div>

            <section>
              <h3 className="font-semibold text-foreground mb-1 sm:mb-2 flex items-center gap-2 text-sm">
                <Scale className="w-4 h-4" />
                Disclaimer of Liability
              </h3>
              <p className="leading-relaxed">
                This website and all information provided are strictly <strong>FOR ENTERTAINMENT 
                AND INFORMATIONAL PURPOSES ONLY</strong>. Not financial, investment, legal, tax, or gambling advice.
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-foreground mb-1 sm:mb-2 text-sm">No Guarantee of Accuracy</h3>
              <p className="leading-relaxed">
                We make <strong>NO WARRANTIES OR GUARANTEES</strong> regarding accuracy or reliability. 
                Data is from third-party sources and may contain errors. Prize availability may change without notice.
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-foreground mb-1 sm:mb-2 text-sm">Assumption of Risk</h3>
              <ul className="list-disc list-inside space-y-1 ml-1">
                <li>Purchases made <strong>ENTIRELY AT YOUR OWN RISK</strong></li>
                <li>Lottery games have <strong>NO GUARANTEED OUTCOME</strong></li>
                <li>Most players <strong>LOSE MONEY</strong> over time</li>
                <li>Statistics do <strong>NOT GUARANTEE</strong> results</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-foreground mb-1 sm:mb-2 text-sm">Limitation of Liability</h3>
              <p className="leading-relaxed">
                We are <strong>NOT LIABLE</strong> for any losses, damages, financial hardship, 
                or harm arising from use of this Service or gambling decisions.
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-foreground mb-1 sm:mb-2 text-sm">Gambling Addiction Warning</h3>
              <div className="p-2 sm:p-3 bg-muted rounded-lg">
                <p className="font-semibold text-xs sm:text-sm">Problem Gambling Helpline</p>
                <p className="text-accent font-mono text-sm sm:text-base">1-800-522-4700</p>
                <p className="text-[10px] sm:text-xs mt-1">Available 24/7, Confidential</p>
              </div>
            </section>

            <section>
              <h3 className="font-semibold text-foreground mb-1 sm:mb-2 text-sm">Additional Terms</h3>
              <ul className="list-disc list-inside space-y-1 ml-1">
                <li>Must be <strong>18+ years old</strong> in Washington State</li>
                <li><strong>NOT AFFILIATED</strong> with WA State Lottery</li>
              </ul>
            </section>

            <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
              <p className="font-semibold text-destructive text-center text-xs sm:text-sm">
                NEVER GAMBLE MORE THAN YOU CAN AFFORD TO LOSE
              </p>
            </div>
          </div>
        </div>

        {/* Footer - Fixed at bottom */}
        <div className="flex-shrink-0 border-t border-border p-4 sm:p-6 pt-4 space-y-3 sm:space-y-4 bg-background">
          <div className="flex items-start space-x-3 w-full">
            <Checkbox 
              id="acknowledge" 
              checked={acknowledged}
              onCheckedChange={setAcknowledged}
              className="mt-0.5"
              data-testid="disclaimer-checkbox"
            />
            <label 
              htmlFor="acknowledge" 
              className="text-xs sm:text-sm leading-relaxed cursor-pointer"
            >
              I have read and understand this disclaimer. I accept full responsibility for my decisions.
            </label>
          </div>
          <Button 
            onClick={handleAccept} 
            disabled={!acknowledged}
            className="w-full"
            size="default"
            data-testid="disclaimer-accept-btn"
          >
            I Understand and Accept
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DisclaimerModal;
